import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
	APP_INITIALIZER,
	type ApplicationConfig,
	importProvidersFrom,
	isDevMode,
	provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, TitleStrategy, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
// Guards are now functions, no need to import classes
import { AuthInterceptor } from '@interceptors/http-interceptor';
import { provideTransloco } from '@jsverse/transloco';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { AuthService } from '@services/auth/auth.service';
import { AuthApiService } from '@services/auth/auth-api.service';
import { ConfigService } from '@services/configuration/config.service';
import { LanguageInitService } from '@services/domain/language-init.service';

import primelocaleEs from 'primelocale/es.json';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { TranslocoTitleStrategy } from './core/i18n/transloco-title.strategy';
import { LucideModule } from './shared/modules/lucide.module';
import { TranslocoHttpLoader } from './transloco-loader';

// «Pulso» preset: ink (#101012) as primary, bone neutrals, pill controls and the
// lime-tinted highlight. Overlays (selects, dialogs, toasts) are appended to <body>,
// so their look must come from these tokens rather than from component styles.
const SupportGKAura = definePreset(Aura, {
	primitive: {
		borderRadius: { none: '0', xs: '6px', sm: '10px', md: '16px', lg: '20px', xl: '28px' },
	},
	semantic: {
		transitionDuration: '120ms',
		focusRing: { width: '2px', style: 'solid', color: '#101012', offset: '2px', shadow: 'none' },
		primary: {
			50: '#FAFAF7',
			100: '#F2F1EC',
			200: '#E2E1DC',
			300: '#A7A6A9',
			400: '#666568',
			500: '#2A2A2E',
			600: '#101012',
			700: '#1C1C1F',
			800: '#101012',
			900: '#101012',
			950: '#101012',
		},
		formField: {
			paddingX: '1rem',
			paddingY: '0.625rem',
			borderRadius: '9999px',
			// Fields show focus with their ink border + lime halo, not an outer ring.
			focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
		},
		list: { padding: '0.375rem', gap: '2px', option: { padding: '0.5rem 0.75rem', borderRadius: '12px' } },
		navigation: { item: { borderRadius: '12px' } },
		overlay: {
			select: { borderRadius: '16px', shadow: '0 20px 40px -24px rgba(16, 16, 18, 0.35)' },
			popover: { borderRadius: '16px', padding: '0.5rem', shadow: '0 20px 40px -24px rgba(16, 16, 18, 0.35)' },
			modal: { borderRadius: '28px', padding: '1.625rem', shadow: '0 40px 80px -32px rgba(16, 16, 18, 0.5)' },
			navigation: { shadow: '0 20px 40px -24px rgba(16, 16, 18, 0.35)' },
		},
		colorScheme: {
			light: {
				surface: {
					0: '#FFFFFF',
					50: '#FAFAF7',
					100: '#F4F3EF',
					200: '#F2F1EC',
					300: '#E2E1DC',
					400: '#A7A6A9',
					500: '#8A898C',
					600: '#666568',
					700: '#2A2A2E',
					800: '#1C1C1F',
					900: '#101012',
					950: '#101012',
				},
				primary: {
					color: '#101012',
					contrastColor: '#F2F1EC',
					hoverColor: '#1C1C1F',
					activeColor: '#2A2A2E',
				},
				highlight: {
					background: '#F6FBE6',
					focusBackground: '#EEF8CC',
					color: '#101012',
					focusColor: '#101012',
				},
				mask: { background: 'rgba(16, 16, 18, 0.28)', color: '#101012' },
				formField: {
					background: '#F2F1EC',
					disabledBackground: '#F4F3EF',
					filledBackground: '#F2F1EC',
					filledHoverBackground: '#F2F1EC',
					filledFocusBackground: '#FFFFFF',
					borderColor: '#E2E1DC',
					hoverBorderColor: '#A7A6A9',
					focusBorderColor: '#101012',
					invalidBorderColor: '#D92D20',
					color: '#101012',
					disabledColor: '#8A898C',
					placeholderColor: '#8A898C',
					invalidPlaceholderColor: '#B42318',
					floatLabelColor: '#8A898C',
					floatLabelFocusColor: '#101012',
					floatLabelActiveColor: '#666568',
					iconColor: '#8A898C',
					shadow: 'none',
				},
				text: { color: '#101012', hoverColor: '#101012', mutedColor: '#666568', hoverMutedColor: '#2A2A2E' },
				content: {
					background: '#FFFFFF',
					hoverBackground: '#FAFAF7',
					borderColor: '#EEEDE8',
					color: '#101012',
				},
				overlay: {
					select: { background: '#FFFFFF', borderColor: '#EEEDE8', color: '#101012' },
					popover: { background: '#FFFFFF', borderColor: '#EEEDE8', color: '#101012' },
					modal: { background: '#FFFFFF', borderColor: 'transparent', color: '#101012' },
				},
				list: {
					option: {
						focusBackground: '#F2F1EC',
						selectedBackground: '#F6FBE6',
						selectedFocusBackground: '#EEF8CC',
						color: '#101012',
						focusColor: '#101012',
						selectedColor: '#101012',
						selectedFocusColor: '#101012',
					},
				},
			},
		},
	},
	components: {
		button: {
			root: { borderRadius: '9999px', paddingX: '1.375rem', paddingY: '0.75rem', label: { fontWeight: '600' } },
		},
		dialog: { root: { borderRadius: '28px' }, title: { fontSize: '22px', fontWeight: '600' } },
		chip: { root: { borderRadius: '9999px' } },
		tag: { root: { borderRadius: '9999px', fontSize: '12px', fontWeight: '700', padding: '0.25rem 0.75rem' } },
		toast: { root: { borderRadius: '20px' } },
		paginator: { navButton: { borderRadius: '9999px' } },
		checkbox: { root: { borderRadius: '7px' } },
		datatable: { headerCell: { padding: '0.625rem 0.875rem' }, bodyCell: { padding: '0.875rem' } },
	},
});

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(routes, withComponentInputBinding()),
		{ provide: TitleStrategy, useClass: TranslocoTitleStrategy },
		// Lucide icons registered once for the whole app, so standalone screens that only
		// import LucideAngularModule (not SharedModule) can render them too.
		importProvidersFrom(BrowserModule, FormsModule, ReactiveFormsModule, LucideModule),
		provideHttpClient(withFetch(), withInterceptors([AuthInterceptor])),
		provideAnimations(),

		providePrimeNG({
			theme: {
				preset: SupportGKAura,
				options: {
					prefix: 'p',
					darkModeSelector: 'none',
					cssLayer: false,
				},
			},
			// Pulso only animates colour, opacity and scale on press.
			ripple: false,
			translation: primelocaleEs.es,
		}),

		{ provide: LocationStrategy, useClass: PathLocationStrategy },
		MessageService,
		AuthService,
		ConfirmationService,
		DynamicDialogRef,
		DynamicDialogConfig,
		DialogService,
		AuthApiService,
		LanguageInitService,
		{
			provide: APP_INITIALIZER,
			useFactory: (configService: ConfigService) => () => configService.loadConfig().toPromise(),
			deps: [ConfigService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: (languageInitService: LanguageInitService) => () => languageInitService.initializeLanguage(),
			deps: [LanguageInitService],
			multi: true,
		},
		provideTransloco({
			config: {
				availableLangs: ['es', 'en'],
				defaultLang: 'es',
				fallbackLang: 'es',
				reRenderOnLangChange: true,
				prodMode: !isDevMode(),
				missingHandler: {
					logMissingKey: false,
					useFallbackTranslation: true,
					allowEmpty: false,
				},
			},
			loader: TranslocoHttpLoader,
		}),

		// Service Worker for PWA - Production only
		provideServiceWorker('ngsw-worker.js', {
			enabled: environment.production,
			registrationStrategy: 'registerWhenStable:30000',
		}),
	],
};
